import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { HabitType } from '../../../types';
import { useHabitContext } from '../../../context/HabitContext';
import { useToast } from '../Toast';
import Button from '../Button';
import Modal from '../Modal';
import ErrorBanner from '../ErrorBanner';
import HabitFormFields from './HabitFormFields';
import './HabitModals.scss';

interface AddHabitModalProps {
  onAdd: (
    name: string,
    type: HabitType,
    color: string,
    temporary: boolean,
    scheduledTime?: string,
  ) => void | Promise<unknown>;
  onClose: () => void;
}

const AddHabitModal: React.FC<AddHabitModalProps> = ({ onAdd, onClose }) => {
  const { formatMessage: t } = useIntl();
  const { categories } = useHabitContext();
  const { showToast } = useToast();
  const [name, setName] = useState('');
  const [type, setType] = useState<HabitType>('checkbox');
  const [color, setColor] = useState('#C25E45');
  const [temporary, setTemporary] = useState(false);
  const [scheduledTime, setScheduledTime] = useState('');
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (timePickerOpen) return;
    if (!name.trim() || saving) return;
    const duplicate = categories
      .flatMap((c) => c.habits)
      .some((h) => h.name.trim().toLowerCase() === name.trim().toLowerCase());
    if (duplicate) {
      setError(t({ id: 'habit.error.duplicateName' }));
      return;
    }

    setError(null);
    setSaving(true);
    try {
      await onAdd(name.trim(), type, color, temporary, scheduledTime || undefined);
      onClose();
      showToast(t({ id: 'toast.habit.added' }));
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message === 'DUPLICATE_NAME') {
        setError(t({ id: 'habit.error.duplicateName' }));
      } else {
        showToast(t({ id: 'toast.habit.saveFailed' }), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <Button variant="primary" size="md" onClick={handleAdd} disabled={saving || timePickerOpen} loading={saving}>
      {t({ id: 'monthly.addForm.add' })}
    </Button>
  );

  return (
    <Modal
      title={t({ id: 'daily.habit.add' })}
      onClose={onClose}
      maxWidth={420}
      panelClassName="habit-modal"
      footer={footer}
    >
      <div className="habit-modal-body">
        {error && <ErrorBanner message={error} />}
        <HabitFormFields
          name={name}
          onNameChange={(value) => {
            setName(value);
            if (error) setError(null);
          }}
          color={color}
          onColorChange={setColor}
          type={type}
          onTypeChange={setType}
          scheduledTime={scheduledTime}
          onScheduledTimeChange={setScheduledTime}
          onTimePickerOpenChange={setTimePickerOpen}
          autoFocusName
          namePlaceholder={t({ id: 'monthly.addForm.namePlaceholder' })}
          onNameKeyDown={(e) => {
            if (e.key === 'Enter' && !timePickerOpen) void handleAdd();
            if (e.key === 'Escape') onClose();
          }}
        >
          <div className="col-edit-section col-edit-section--temporary">
            <div className="temporary-habit-label">
              <label className="temporary-habit-option">
                <input
                  type="checkbox"
                  className="temporary-habit-checkbox"
                  checked={temporary}
                  onChange={(e) => setTemporary(e.target.checked)}
                />
                <span>{t({ id: 'monthly.addForm.temporary' })}</span>
              </label>
              <button
                type="button"
                className="temporary-habit-tooltip-icon"
                aria-label={t({ id: 'monthly.addForm.temporaryTooltip' })}
                title={t({ id: 'monthly.addForm.temporaryTooltip' })}
                data-tooltip={t({ id: 'monthly.addForm.temporaryTooltip' })}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                ?
              </button>
            </div>
          </div>
        </HabitFormFields>
      </div>
    </Modal>
  );
};

export default AddHabitModal;
